import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import {
  makeStyles,
  tokens,
  Text,
  Button,
  TabList,
  Tab,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  MenuDivider,
  Tooltip,
  type SelectTabData,
  type SelectTabEvent,
} from '@fluentui/react-components';
import {
  DismissRegular,
  HistoryRegular,
  MoreHorizontalRegular,
  AddRegular,
  EraserRegular,
  ArrowUndoRegular,
  ArrowRedoRegular,
  PanelRightContractRegular,
  BeakerRegular,
} from '@fluentui/react-icons';
import { useSessionHistoryStore } from '@/stores/sessionHistoryStore';
import { useWorkflowStore } from '@/stores/workflowStore';
import { ChatHistoryPanel } from './ChatHistoryPanel';
import { ContextStatusIndicator } from './ContextStatusIndicator';
import { ScopeBanner, type EditScopeInfo, type NodeInfo } from './ScopeBanner';
import { StatusList } from './StatusList';
import { ChatInput } from './ChatInput';
import { GitHubAuthBar } from './GitHubAuthBar';
import { useAgentChatAguiStore, type StickyEditScope } from '@/stores/agentChatAguiStore';
import { useAppStore } from '@/stores/appStore';
import { useAuth } from '@/auth/useAuth';
import { useConnectorContext } from '@/contexts/ConnectorContext';
import * as chatHistoryClient from '@/services/chatHistoryClient';
import type { ChatHistoryFetchOptions } from '@/services/chatHistoryClient';
import { isLocalChatHistoryRuntime } from '@/services/chatHistoryClient';
import type { AzureContext } from '@/types/agui';
import { averageColors, DEFAULT_SELECTION_COLOR } from '@/utils/primitives';
import { resolveNodePath } from '@/utils/json';
import { parseAppId } from '@/types/logicApps';
import { useRuntimeDetails } from '@/hooks/useAppDetails';
import { CopilotLogoIcon } from '@/components/common/CopilotIcon';
import { extractAzureRegion } from '@/services/logicAppsApi';
import { useDraftModeStore, selectIsReadOnly } from '@/stores/draftModeStore';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';

type AssistantTab = 'current' | 'history';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '100%',
    minHeight: 0,
    overflow: 'hidden',
    backgroundColor: tokens.colorNeutralBackground1,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground2,
    minHeight: '40px',
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
  },
  tabContent: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  chatContent: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  historyContent: {
    flex: 1,
    minHeight: 0,
    overflow: 'auto',
  },
  errorBanner: {
    padding: tokens.spacingVerticalS,
    paddingInline: tokens.spacingHorizontalM,
    backgroundColor: tokens.colorStatusDangerBackground1,
    color: tokens.colorStatusDangerForeground1,
    borderBottom: `1px solid ${tokens.colorStatusDangerBorder1}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalS,
    minHeight: '36px',
  },
  experimentalBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    paddingBlock: tokens.spacingVerticalXS,
    paddingInline: tokens.spacingHorizontalM,
    backgroundColor: tokens.colorNeutralBackground3,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  experimentalIcon: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorPaletteMarigoldForeground2,
    flexShrink: 0,
  },
  experimentalText: {
    color: tokens.colorNeutralForeground3,
  },
});

interface ChatSidebarProps {
  isOpen: boolean;
  workflowName: string;
  /** Hide the header (used when rendered standalone) */
  hideHeader?: boolean;
  /** Callback to close the panel (passed from SidePanel) */
  onClose?: () => void;
  /** Callback to minimize the panel (passed from SidePanel) */
  onMinimize?: () => void;
}

export const ChatSidebar = ({ isOpen, workflowName, hideHeader = false, onClose, onMinimize }: ChatSidebarProps) => {
  const styles = useStyles();
  const [localError, setLocalError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<AssistantTab>('current');
  const isAssistantGitHubAndHistoryEnabled = useFeatureFlag('assistantGitHubAndHistory');
  const isScopedEditEnabled = useFeatureFlag('scopedEdit');

  const { getArmToken, getOttoToken } = useAuth();
  const { selectedProject, selectedApp } = useAppStore();
  const { data: appDetails } = useRuntimeDetails();
  // Read draft-mode read-only state directly from the store so this works
  // regardless of where the DraftModeProvider lives in the tree.
  const isReadOnly = useDraftModeStore(selectIsReadOnly);
  const armResourceId = selectedApp?.armResourceId ?? selectedApp?.properties?.appResourceId;
  const parsedAppId = useMemo(() => armResourceId ? parseAppId(armResourceId) : null, [armResourceId]);

  // Undo/Redo functionality from session history store
  const canUndo = useSessionHistoryStore((state) => state.canUndo());
  const canRedo = useSessionHistoryStore((state) => state.canRedo());
  const undo = useSessionHistoryStore((state) => state.undo);
  const redo = useSessionHistoryStore((state) => state.redo);

  const handleTabSelect = useCallback((_event: SelectTabEvent, data: SelectTabData) => {
    setActiveTab(data.value as AssistantTab);
  }, []);

  // Undo handler - session history auto-captures all changes, so undo directly
  const handleUndoClick = useCallback(() => {
    undo();
  }, [undo]);

  // Build fetch options for chat history API
  const getFetchOptions = useCallback(async (): Promise<ChatHistoryFetchOptions | undefined> => {
    const token = await getArmToken();
    if (!token) {
      return undefined;
    }

    if (isLocalChatHistoryRuntime()) {
      // In local mode, only accessToken is needed (URL is built from localhost)
      return {
        projectName: '', // Not needed in local mode but keeping for type compatibility
        appName: '', // Not needed in local mode but keeping for type compatibility
        accessToken: token,
      };
    }

    if (!armResourceId) {
      return undefined;
    }
    if (!parsedAppId) {
      return undefined;
    }
    return {
      projectName: selectedProject ?? '',
      appName: selectedApp?.name ?? '',
      accessToken: token,
    };
  }, [getArmToken, armResourceId, parsedAppId, selectedProject, selectedApp?.name]);

  const {
    chatMessages,
    currentStatus,
    currentTurnStages,
    currentContent,
    pendingClarification,
    isStreaming,
    error,
    currentChatId,
    sendMessage,
    cancelStream,
    clearChat,
    clearChatMessages,
    clearError,
    saveCurrentChat,
    loadChat,
    answerClarification,
  } = useAgentChatAguiStore();

  const selectedNodes = useWorkflowStore((s) => s.selectedNodes);
  const removeFromSelection = useWorkflowStore((s) => s.removeFromSelection);

  // Get connector info for color and icon lookup
  const connectorContext = useConnectorContext();
  const getConnectorInfo = useCallback((connectionName: string) => {
    return connectorContext.getConnectorInfo(connectionName);
  }, [connectorContext]);

  // STICKY EDIT SCOPE:
  // - Starts as 'workflow' mode
  // - Auto-enters node edit mode when nodes are selected
  // - Stays in node edit mode until user explicitly clicks "Exit"
  const [stickyEditScope, setStickyEditScope] = useState<EditScopeInfo>({ type: 'workflow' });

  // Track the previous selection to detect new selections
  const prevSelectedNodesRef = useRef<string[]>([]);

  // Track last saved message count to avoid redundant saves
  const lastSavedMessageCountRef = useRef<number>(0);

  // Sync stickyEditScope with selectedNodes (enter edit mode on new selection, update on deselect)
  // Gated by scopedEdit flag — when off, scope always stays at 'workflow'
  useEffect(() => {
    if (!isScopedEditEnabled) {
      return;
    }

    const currentNodeNames = selectedNodes.map(n => n.name);
    const prevNodeNames = prevSelectedNodesRef.current;
    
    // Check if selection changed at all
    const selectionChanged = currentNodeNames.length !== prevNodeNames.length ||
      currentNodeNames.some(name => !prevNodeNames.includes(name)) ||
      prevNodeNames.some(name => !currentNodeNames.includes(name));
    
    if (!selectionChanged) {
      return;
    }
    
    // If no nodes selected, exit to workflow mode
    if (currentNodeNames.length === 0) {
      setStickyEditScope({ type: 'workflow' });
      prevSelectedNodesRef.current = currentNodeNames;
      return;
    }
    
    // Build per-node info with colors and icons
    const nodesWithInfo: NodeInfo[] = selectedNodes.map(node => {
      let color = DEFAULT_SELECTION_COLOR;
      let iconUri: string | undefined;
      
      if (node.connectionName) {
        const info = getConnectorInfo(node.connectionName);
        if (info?.brandColor) {
          color = info.brandColor;
        }
        if (info?.iconUri) {
          iconUri = info.iconUri;
        }
      }
      // Only include iconUri if it's defined (for exactOptionalPropertyTypes)
      return iconUri 
        ? { name: node.name, color, iconUri }
        : { name: node.name, color };
    });

    // Also calculate average color for single node mode
    const colors = nodesWithInfo.map(n => n.color).filter(c => c !== DEFAULT_SELECTION_COLOR);
    const avgColor = colors.length > 0 ? (averageColors(colors) ?? DEFAULT_SELECTION_COLOR) : DEFAULT_SELECTION_COLOR;

    // Get icon for single node
    const singleNodeIcon = nodesWithInfo.length === 1 ? nodesWithInfo[0]?.iconUri : undefined;

    // Resolve JSON path for single-node selection
    let jsonPath: string | undefined;
    if (selectedNodes.length === 1) {
      const node = selectedNodes[0]!;
      const workflow = useWorkflowStore.getState().workflow;
      if (workflow) {
        const resolved = resolveNodePath(
          workflow as unknown as Record<string, unknown>,
          node.name,
          node.type,
          node.parentAgentName,
        );
        if (resolved) {
          jsonPath = resolved;
        }
      }
    }

    // Enter/update edit mode for these nodes
    setStickyEditScope({
      type: selectedNodes.length === 1 ? 'single' : 'multi',
      nodeNames: currentNodeNames,
      color: avgColor,
      ...(singleNodeIcon && { iconUri: singleNodeIcon }),
      ...(jsonPath ? { jsonPath } : {}),
      nodes: nodesWithInfo,
    });
    
    // Update ref for next comparison
    prevSelectedNodesRef.current = currentNodeNames;
  }, [selectedNodes, getConnectorInfo, isScopedEditEnabled]);

  // Handle explicit exit from edit mode
  const handleExitEditMode = useCallback(() => {
    setStickyEditScope({ type: 'workflow' });
  }, []);

  // Handle removing a single node from the multi-selection
  const handleRemoveNode = useCallback((nodeName: string) => {
    // Remove from workflow store selection
    removeFromSelection(nodeName);
    
    // Update sticky scope
    setStickyEditScope(prev => {
      if (prev.type !== 'multi' || !prev.nodes) return prev;
      
      const remainingNodes = prev.nodes.filter(n => n.name !== nodeName);
      const remainingNames = prev.nodeNames?.filter(n => n !== nodeName) || [];
      
      // If only 1 node left, switch to single mode
      if (remainingNodes.length === 1) {
        const singleNode = remainingNodes[0];
        const result: EditScopeInfo = {
          type: 'single',
          nodeNames: remainingNames,
          color: singleNode?.color || DEFAULT_SELECTION_COLOR,
          nodes: remainingNodes,
        };
        if (singleNode?.iconUri) {
          result.iconUri = singleNode.iconUri;
        }
        return result;
      }
      
      // If no nodes left, exit to workflow mode
      if (remainingNodes.length === 0) {
        return { type: 'workflow' };
      }
      
      // Still multi-node
      return {
        ...prev,
        nodeNames: remainingNames,
        nodes: remainingNodes,
      };
    });
  }, [removeFromSelection]);

  // Track if we've already loaded the initial chat for this workflow
  const hasLoadedInitialChat = useRef(false);

  // Load the latest chat on first mount (only if no chat is currently loaded)
  useEffect(() => {
    const loadLatestChat = async () => {
      if (!workflowName || currentChatId || hasLoadedInitialChat.current) return;
      
      hasLoadedInitialChat.current = true;
      
      try {
        const opts = await getFetchOptions();
        const response = await chatHistoryClient.listChats(workflowName, opts);
        if (response.chats.length > 0) {
          // Load the most recent chat (first in the list, sorted by updatedAt desc)
          const firstChat = response.chats[0];
          if (firstChat) {
            loadChat(workflowName, firstChat.id, opts);
          }
        }
      } catch (err) {
        // In local mode, chat history APIs may not be available — this is expected
        if (!parsedAppId || parsedAppId.subscriptionId === 'local-subscription') return;
        console.error('Failed to load latest chat:', err);
      }
    };

    loadLatestChat();
  }, [workflowName, currentChatId, loadChat, getFetchOptions, parsedAppId]);

  // Auto-save chat when message count actually changes (debounced effect)
  // Skip in local mode where chat history APIs aren't available
  useEffect(() => {
    // Skip save in local mode (fake subscription IDs cause 400 errors that steal focus)
    if (!parsedAppId || parsedAppId.subscriptionId === 'local-subscription') return;

    // Only save if:
    // 1. We have messages
    // 2. We have a workflow name
    // 3. Not currently streaming
    // 4. Message count has CHANGED since last save
    const messageCount = chatMessages.length;
    if (messageCount > 0 && workflowName && !isStreaming && messageCount !== lastSavedMessageCountRef.current) {
      // Save after a short delay to debounce rapid updates
      const saveTimeout = setTimeout(async () => {
        // Double-check message count hasn't changed during the delay
        if (chatMessages.length === messageCount) {
          lastSavedMessageCountRef.current = messageCount;
          const opts = await getFetchOptions();
          saveCurrentChat(workflowName, opts);
        }
      }, 2000);

      return () => clearTimeout(saveTimeout);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally excluding function refs to prevent infinite loops
  }, [chatMessages.length, workflowName, isStreaming, parsedAppId]);

  const handleSendMessage = (content: string) => {
    if (!workflowName) {
      setLocalError('No workflow selected');
      return;
    }

    if (isReadOnly) {
      setLocalError('Switch to draft mode to edit workflow');
      return;
    }

    // Clear any previous local error
    setLocalError(null);

    // Build Azure context from selected app and workflow
    // In local mode, selectedApp may be null — use fallback values
    const azureContext: AzureContext = {
      subscriptionId: parsedAppId?.subscriptionId ?? 'local-subscription',
      resourceGroup: parsedAppId?.resourceGroup ?? 'local-resource-group',
      siteName: parsedAppId?.appName ?? 'local-runtime',
      location: extractAzureRegion(appDetails?.hostName ?? '') ?? 'local',
      workflowName,
      hostName: appDetails?.hostName ?? '',
    };

    // Convert EditScopeInfo to StickyEditScope for the store
    const scopeForMessage: StickyEditScope = {
      type: stickyEditScope.type,
      ...(stickyEditScope.nodeNames && { nodeNames: stickyEditScope.nodeNames }),
      ...(stickyEditScope.jsonPath ? { jsonPath: stickyEditScope.jsonPath } : {}),
      ...(stickyEditScope.color && { color: stickyEditScope.color }),
    };

    sendMessage(content, getOttoToken, azureContext, scopeForMessage);
  };

  /**
   * Stable callback the ClarificationCard invokes when the user picks an
   * option or sends custom text. Reuses the same Azure context resolution
   * as handleSendMessage so the answer flows through the normal pipeline.
   */
  const handleAnswerClarification = useCallback((text: string, optionId?: string) => {
    if (!workflowName) {
      setLocalError('No workflow selected');
      return;
    }
    const azureContext: AzureContext = {
      subscriptionId: parsedAppId?.subscriptionId ?? 'local-subscription',
      resourceGroup: parsedAppId?.resourceGroup ?? 'local-resource-group',
      siteName: parsedAppId?.appName ?? 'local-runtime',
      location: extractAzureRegion(appDetails?.hostName ?? '') ?? 'local',
      workflowName,
      hostName: appDetails?.hostName ?? '',
    };
    void answerClarification(text, optionId, getOttoToken, azureContext);
  }, [workflowName, parsedAppId, appDetails?.hostName, answerClarification, getOttoToken]);

  const handleStopStreaming = () => {
    cancelStream();
  };

  // Handler when a chat is selected from history
  const handleOpenChat = useCallback(() => {
    // Chat is loaded by ChatHistoryPanel with proper auth
    // Switch back to current tab to show the loaded chat
    setActiveTab('current');
  }, []);

  // Handler when new chat is started from history
  const handleNewChat = useCallback(() => {
    clearChat();
    setActiveTab('current');
  }, [clearChat]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className={styles.container} data-chat-sidebar>
      {/* Merged header: tabs on left, overflow menu + close on right */}
      {!hideHeader && (
        <div className={styles.header}>
          <TabList
            selectedValue={activeTab}
            onTabSelect={handleTabSelect}
            size="small"
          >
            <Tab value="current" icon={<CopilotLogoIcon fontSize={20} />}>
              Copilot
            </Tab>
            {isAssistantGitHubAndHistoryEnabled && (
              <Tab value="history" icon={<HistoryRegular />}>
                History
              </Tab>
            )}
          </TabList>

          <div className={styles.headerActions}>
            <Menu>
              <MenuTrigger disableButtonEnhancement>
                <Tooltip content="More options" relationship="label">
                  <Button
                    appearance="subtle"
                    size="small"
                    icon={<MoreHorizontalRegular />}
                    aria-label="More options"
                  />
                </Tooltip>
              </MenuTrigger>
              <MenuPopover>
                <MenuList>
                  <MenuItem
                    icon={<AddRegular />}
                    onClick={handleNewChat}
                  >
                    New session
                  </MenuItem>
                  <MenuItem
                    icon={<EraserRegular />}
                    onClick={clearChatMessages}
                    disabled={chatMessages.length === 0 || isReadOnly}
                  >
                    Clear chat
                  </MenuItem>
                  <MenuDivider />
                  <MenuItem
                    icon={<ArrowUndoRegular />}
                    onClick={handleUndoClick}
                    disabled={!canUndo || isReadOnly}
                  >
                    Undo
                  </MenuItem>
                  <MenuItem
                    icon={<ArrowRedoRegular />}
                    onClick={redo}
                    disabled={!canRedo || isReadOnly}
                  >
                    Redo
                  </MenuItem>
                </MenuList>
              </MenuPopover>
            </Menu>

            {onMinimize && (
              <Tooltip content="Minimize" relationship="label">
                <Button
                  appearance="subtle"
                  size="small"
                  icon={<PanelRightContractRegular />}
                  onClick={onMinimize}
                  aria-label="Minimize panel"
                />
              </Tooltip>
            )}

            {onClose && (
              <Tooltip content="Close" relationship="label">
                <Button
                  appearance="subtle"
                  size="small"
                  icon={<DismissRegular />}
                  onClick={onClose}
                  aria-label="Close panel"
                />
              </Tooltip>
            )}
          </div>
        </div>
      )}

      {/* Experimental banner */}
      <div className={styles.experimentalBanner} role="status">
        <BeakerRegular className={styles.experimentalIcon} />
        <Text size={200} className={styles.experimentalText}>
          This feature is experimental. Responses may be inaccurate.
        </Text>
      </div>

      {/* Tab content */}
      <div className={styles.tabContent}>
        {activeTab === 'current' || !isAssistantGitHubAndHistoryEnabled ? (
          <div className={styles.chatContent}>
            <ContextStatusIndicator />

            {(error || localError) && (
              <div className={styles.errorBanner}>
                <Text size={200}>{localError || error}</Text>
                <Button
                  appearance="subtle"
                  size="small"
                  icon={<DismissRegular />}
                  onClick={() => {
                    if (localError) setLocalError(null);
                    else clearError();
                  }}
                  aria-label="Dismiss error"
                />
              </div>
            )}

            <StatusList
              chatMessages={chatMessages}
              currentStatus={currentStatus}
              isStreaming={isStreaming}
              streamingContent={currentContent}
              currentTurnStages={currentTurnStages}
              pendingClarification={pendingClarification}
              onAnswerClarification={handleAnswerClarification}
            />

            {isScopedEditEnabled && (
              <ScopeBanner
                editScope={stickyEditScope}
                onExitEditMode={handleExitEditMode}
                onRemoveNode={handleRemoveNode}
              />
            )}

            {isAssistantGitHubAndHistoryEnabled && <GitHubAuthBar />}

            <ChatInput
              onSendMessage={handleSendMessage}
              onStopStreaming={handleStopStreaming}
              isStreaming={isStreaming}
              disabled={isReadOnly || !!pendingClarification}
              {...((isReadOnly || !!pendingClarification) && {
                placeholder: pendingClarification
                  ? 'Please answer the question above to continue…'
                  : 'Switch to draft mode to edit workflow',
              })}
              messageHistory={chatMessages.filter(m => m.role === 'user').map(m => m.content)}
              isFullWorkflowScope={stickyEditScope.type === 'workflow'}
            />
          </div>
        ) : (
          <div className={styles.historyContent}>
            <ChatHistoryPanel
              workflowName={workflowName}
              activeChatId={currentChatId ?? undefined}
              onOpenChat={handleOpenChat}
              onNewChat={handleNewChat}
              isVisible={activeTab === 'history'}
            />
          </div>
        )}
      </div>
    </div>
  );
};
